import type { CSSProperties, FC } from 'react'
import { useFrontendTool } from '@copilotkit/react-core/v2'
import { ToolCallStatus } from '@copilotkit/core'
import { z } from 'zod'
import { cardStyle, FONT, TEXT, DIMMED, BRAND, BORDER } from './widgetStyles'
import { formatCompactNumber } from '@/lib/formatNumber'

// ---- styles -----------------------------------------------------------------
const titleStyle: CSSProperties = {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: 'var(--mantine-font-size-md)',
    lineHeight: 1.35,
    color: TEXT,
}
const labelStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    color: TEXT,
    lineHeight: 1.4,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}
const valueStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    fontWeight: 600,
    color: DIMMED,
    flexShrink: 0,
    marginLeft: 12,
}
// The bar track (full width) and its filled portion. Inline-styled so it
// survives the chat bubble's CSS reset (see widgetStyles).
const trackStyle: CSSProperties = {
    width: '100%',
    height: 10,
    borderRadius: 9999,
    background: 'var(--mantine-color-default-hover)',
    border: `1px solid ${BORDER}`,
    overflow: 'hidden',
}

const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v : undefined

const num = (v: unknown): number | undefined =>
    typeof v === 'number' && isFinite(v) ? v : undefined

interface Bar {
    label?: string
    value?: number
    display_value?: string
}

// Display-only chat widget: a simple horizontal bar chart. Each bar's fill is
// scaled to the largest value so the user can compare lengths at a glance. Read
// defensively (args arrive as raw JSON and stream in progressively) so a
// malformed/partial item never throws while rendering.
export const BarChart: FC<{ args: Record<string, unknown> }> = ({ args }) => {
    const title = str((args as { title?: unknown }).title)
    const raw = (args as { bars?: unknown }).bars
    const bars: Bar[] = Array.isArray(raw)
        ? (raw.filter((b) => b && typeof b === 'object') as Bar[])
              .map((b) => ({
                  label: str(b.label),
                  value: num(b.value),
                  display_value: str(b.display_value),
              }))
              .filter((b) => b.label)
              // Sort largest value first so the bars read top-to-bottom by size.
              .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
        : []

    if (!title && !bars.length) return null

    // Scale fills to the largest (non-negative) value. Negative values clamp to
    // 0 width — the chart is for comparing magnitudes, not signed deltas.
    const max = Math.max(0, ...bars.map((b) => b.value ?? 0))

    return (
        <div style={cardStyle}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--mantine-spacing-md)',
                }}
            >
                {title && <div style={titleStyle}>{title}</div>}
                {bars.length > 0 && (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--mantine-spacing-sm)',
                        }}
                    >
                        {bars.map((b) => {
                            const value = b.value ?? 0
                            const pct =
                                max > 0
                                    ? Math.max(0, Math.min(1, value / max)) *
                                      100
                                    : 0
                            const shown =
                                b.display_value ??
                                (b.value != null
                                    ? formatCompactNumber(b.value)
                                    : '')
                            return (
                                <div
                                    key={`${b.label}:${b.display_value ?? value}`}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={labelStyle}>
                                            {b.label}
                                        </span>
                                        {shown && (
                                            <span style={valueStyle}>
                                                {shown}
                                            </span>
                                        )}
                                    </div>
                                    {/* Decorative: the label + value above
                                        already convey the data textually. */}
                                    <div style={trackStyle} aria-hidden>
                                        <div
                                            style={{
                                                width: `${pct}%`,
                                                height: '100%',
                                                background: BRAND,
                                                borderRadius: 9999,
                                                transition: 'width 200ms ease',
                                            }}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

// Parameters the agent fills when calling `show_bar_chart`.
const barChartSchema = z.object({
    title: z
        .string()
        .optional()
        .describe(
            'Optional short heading describing what is being compared (e.g. "Reach by retailer").'
        ),
    bars: z
        .array(
            z.object({
                label: z
                    .string()
                    .describe('Short label for this bar (the category).'),
                value: z
                    .number()
                    .describe(
                        'The numeric value this bar represents — bars are scaled relative to the largest value.'
                    ),
                display_value: z
                    .string()
                    .optional()
                    .describe(
                        'Optional pre-formatted value to show (e.g. "$1.2M", "45%"). If omitted, `value` is shown formatted compactly (e.g. "877K").'
                    ),
            })
        )
        .describe(
            '2 or more bars to compare (keep it scannable; ~8 is plenty).'
        ),
})

// Registers `show_bar_chart` as a CLIENT-SIDE (non-HITL) display-only frontend
// tool, exposed to the backend agents via the AGUIToolset. Like
// show_recommended_strategies it does NOT wait for input: the handler returns
// immediately (resolving the proxied long-running call so the agent continues)
// while `render` draws the chart. Call once from a component inside the
// CopilotKit provider tree (ThreadPage).
export function useBarChartTool(): void {
    useFrontendTool(
        {
            name: 'show_bar_chart',
            description: `Draw a simple horizontal bar chart to show a breakdown or compare values — a picture is worth a thousand words. PREFER this over a table or prose whenever you're showing the user how values compare: reach/size across segments, datasets, brands, or categories; a distribution; before/after; any "X vs Y vs Z" numbers.

Pass:
- \`title\`: optional short heading for what's being compared.
- \`bars\`: the values to compare, each with a \`label\` (category), a numeric \`value\` (bars are scaled to the largest), and an optional \`display_value\` (pre-formatted text like "$1.2M" or "45%"; otherwise the value is shown compactly).

It is DISPLAY-ONLY — it renders the chart and does not ask a question or wait for input. Don't also restate the same numbers in prose.`,
            parameters: barChartSchema,
            // Display-only: nothing to compute. CopilotKit does NOT validate
            // incoming args against `parameters`, so we safeParse here and, on a
            // mismatch, RESOLVE with an error string (never reject — a rejection
            // is treated as a handler crash and suppresses the agent's follow-up
            // run). On success the resolved promise unblocks the backend's
            // (long-running) proxied call so the agent continues.
            handler: (args) => {
                const parsed = barChartSchema.safeParse(args)
                if (!parsed.success) {
                    return Promise.resolve(
                        `Invalid arguments for show_bar_chart — they do not match the schema:\n${z.prettifyError(parsed.error)}\nPlease call the tool again with arguments that match the schema.`
                    )
                }
                return Promise.resolve('Rendered to the user.')
            },
            render: ({ args, status }) => {
                // Don't render a failed tool call: once args finish streaming,
                // suppress the chart if they don't match the schema (the handler
                // returns the matching validation error to the agent). Partial
                // args mid-stream still render progressively.
                if (
                    status !== ToolCallStatus.InProgress &&
                    !barChartSchema.safeParse(args).success
                ) {
                    return null
                }
                return <BarChart args={args} />
            },
        },
        []
    )
}
